import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Users, ShieldCheck, ShieldOff, Eye, LogIn } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);

  const load = () => Promise.all([api.get("/admin/users"), api.get("/admin/stats")])
    .then(([u, s]) => { setUsers(u.data); setStats(s.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const setRole = async (uid, role) => {
    try { await api.post(`/admin/users/${uid}/role`, { role }); toast.success(`Role updated to ${role}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const impersonate = async (uid) => {
    try { await api.post(`/admin/impersonate/${uid}`); toast.success("Now acting as user"); window.location.href = "/dashboard"; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const viewOverview = async (uid) => {
    try { const { data } = await api.get(`/admin/users/${uid}/overview`); setOverview(data); } catch { toast.error("Failed to load"); }
  };

  const CARDS = stats ? [["Users", stats.users], ["Admins", stats.admins], ["Songs", stats.songs], ["Open Tickets", stats.open_tickets]] : [];

  return (
    <div data-testid="admin-users-page">
      <div className="mb-8"><h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-primary mb-2">Admin</h4><h1 className="font-heading text-4xl font-black tracking-tighter uppercase">Users</h1></div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {CARDS.map(([l, v]) => (
          <div key={l} className="bg-card border border-border rounded-lg p-6"><div className="font-mono text-3xl font-semibold">{v}</div><div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{l}</div></div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {users.length === 0 ? <div className="text-center py-14"><Users className="h-10 w-10 text-zinc-700 mx-auto mb-3" /><p className="text-zinc-500">No users.</p></div> : (
          <Table>
            <TableHeader><TableRow className="border-border hover:bg-transparent">{["User", "Role", "Songs", "Deals", "Royalties", "Actions"].map((h) => <TableHead key={h} className={`text-zinc-500 uppercase text-xs tracking-widest ${h === "Actions" ? "text-right" : ""}`}>{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{users.map((u) => (
              <TableRow key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="border-border row-in">
                <TableCell><div className="font-semibold">{u.name || "—"}</div><div className="text-xs text-zinc-500">{u.email}</div></TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded border font-semibold ${u.role === "admin" ? "text-primary border-primary/40 bg-primary/10" : "text-zinc-400 border-border bg-secondary"}`}>{u.role}</span></TableCell>
                <TableCell className="font-mono">{u.songs}</TableCell>
                <TableCell className="font-mono">{u.deals}</TableCell>
                <TableCell className="font-mono text-primary">${(u.royalties_total || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => viewOverview(u.user_id)} data-testid={`view-user-${u.user_id}`} title="View overview" className="text-zinc-400 hover:text-primary p-2"><Eye className="h-4 w-4" /></button>
                  <button onClick={() => impersonate(u.user_id)} data-testid={`impersonate-${u.user_id}`} title="Act as user" className="text-zinc-400 hover:text-cyan-300 p-2"><LogIn className="h-4 w-4" /></button>
                  {u.role === "admin"
                    ? <button onClick={() => setRole(u.user_id, "user")} data-testid={`demote-${u.user_id}`} title="Revoke admin" className="text-zinc-400 hover:text-amber-400 p-2"><ShieldOff className="h-4 w-4" /></button>
                    : <button onClick={() => setRole(u.user_id, "admin")} data-testid={`promote-${u.user_id}`} title="Make admin" className="text-zinc-400 hover:text-primary p-2"><ShieldCheck className="h-4 w-4" /></button>}
                </TableCell>
              </TableRow>))}</TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!overview} onOpenChange={(o) => !o && setOverview(null)}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{overview?.user?.name} · {overview?.user?.email}</DialogTitle></DialogHeader>
          {overview && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[["Songs", overview.counts.songs], ["Deals", overview.counts.deals], ["Royalties", `$${overview.counts.royalties_total.toLocaleString()}`]].map(([l, v]) => (
                  <div key={l} className="bg-secondary border border-border rounded-md p-3"><div className="font-mono text-lg">{v}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div></div>
                ))}
              </div>
              <div><div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Recent Songs</div>
                {overview.songs.length ? overview.songs.map((s, i) => <div key={i} className="text-sm py-1 border-b border-border/50">{s.title} <span className="font-mono text-xs text-zinc-500">{s.isrc || "—"}</span></div>) : <p className="text-sm text-zinc-500">None</p>}
              </div>
              <Button onClick={() => impersonate(overview.user.user_id)} className="w-full bg-primary text-primary-foreground font-bold hover:bg-primary/90"><LogIn className="h-4 w-4 mr-2" />Act as this user</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
